import { expandDrawer, logout } from '@/store/actions';
import { palette } from '@/theme/constants';
import { AppBar, Avatar, Box, IconButton, Paper, Typography } from '@mui/material';
import { motion } from 'framer-motion';

import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styles } from '../styles';

import { UseIntermitence } from '@/hooks';
import {
  authSelector,
  backgroundChangeSelector,
  hideGradientSelector,
  homeSelector,
  notificationsSelector,
  storySelector,
} from '@/store/selectors';
import { cdn_url, GetInitials, logoutWithFacebook } from '@/utils';
import { CancelModal } from '../CancelModal';
import Search from '../Search';
import { getCollaboratorsOptions, getPropmtsOptions } from '../constants';
import DrawerComponent from '@/components/DrawerComponent';
import Image from 'next/image';
import DropDownAppBar from '@/components/DropDownAppBar';
import NotificationBadge from '../../../../public/icons/components/notificationBadge';
const MotionBox = motion(Box);
const MotionInputContainer = motion(Box);

export const MuiAppBarMobile: FC<any> = ({ adminPalette, search, setSearch }) => {
  const dispatch = useDispatch();
  const hideGradient = useSelector(hideGradientSelector);
  const backgroundChange = useSelector(backgroundChangeSelector);

  const { user } = useSelector(authSelector);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [showHomeElements, setShowHomeElements] = useState(true);
  const notifications = useSelector(notificationsSelector);
  const { status, switchStatus } = UseIntermitence();
  const { stories } = useSelector(homeSelector);
  const { story } = useSelector(storySelector);
  const prompts = getPropmtsOptions(stories, story);
  const collaborators = getCollaboratorsOptions(user?.collaborators || [], story);
  const [notificationsData, setNotificationsData] = useState<any>([]);
  const [notificationsType, setNotificationsType] = useState<any>([]);

  const handleDrawerChange = () => {
    dispatch(expandDrawer());
  };

  const setShowDropdown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((isOpen) => !isOpen);
  };

  const setShowNotification = (event: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    setOpenNotification((openNotification) => !openNotification);
  };

  const setShowFilters = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenFilters((openFilters) => !openFilters);
  };

  const handleClose = (event: any) => {
    if (event?.target?.alt !== 'settings') setIsOpen(false);
  };
  const handleCloseNotifications = (event: any) => {
    if (event?.target?.alt !== 'notifications') setOpenNotification(false);
  };

  const handleCloseFilters = (event: any) => {
    if (event?.target?.alt !== 'filters') setOpenFilters(false);
  };

  useEffect(() => {
    if (router.pathname == '/app/home') return setShowHomeElements(true);
    setShowHomeElements(false);
  }, [router.pathname]);

  const getSettingsMobileOptions = [
    {
      label: 'notifications',
      action: () => {
        setShowNotification(event);
        setNotificationsType('notification');
        setNotificationsData(notifications?.notifications?.allNotifications || [] || []);
      },
    },
    {
      label: 'collaborators',
      action: () => {
        setShowNotification(event);
        setNotificationsType('collaborator');
        setNotificationsData(notifications?.notifications?.collaborationNotifications || []);
      },
    },

    {
      label: 'logout',
      action: async () => {
        dispatch(logout());
        router.push('');
        await logoutWithFacebook();
      },
    },
  ];

  const closeProcess = () => {
    if (
      router.pathname?.includes('memory/create') ||
      (router.pathname?.includes('story') && router.pathname?.includes('update'))
    )
      return router.push(`/app/story/${story?.url}`);
    else router.push('/app/home');
  };
  const iconSrc = router.pathname === '/app/story/[id]' ? '/images/logo-new-white.svg' : '/images/logo-new.svg';

  function addOpacityToHex(hexColor: string, opacity: number): string {
    const alpha = Math.min(Math.max(opacity, 0), 1);

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const accentColorWithOpacity = addOpacityToHex(adminPalette.accentColor, 0.3);

  const buttonBackground = router.pathname === '/app/story/[id]' ? accentColorWithOpacity : palette?.cardBackground;

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          background: backgroundChange
            ? hideGradient
              ? 'none'
              : 'linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.25) 35%)'
            : 'transparent',
          backdropFilter: 'blur(1.562rem)',
          borderBottom: '2px solid #0000001A',
        }}>
        <Box>
          <Box display={'flex'} padding={'1rem'} justifyContent={'space-between'} alignItems={'center'}>
            <Box display={'flex'} flexDirection={'row'}>
              <Image
                onClick={() => router.push('/app/home')}
                style={{ cursor: 'hover' }}
                src={iconSrc}
                priority
                fetchPriority='high'
                alt={'logo'}
                width={40}
                height={40}
                quality={80}
              />
              <Box
                display={'flex'}
                justifyContent={'flex-start'}
                flexGrow={isExpanded ? 1 : 0}
                zIndex={2}
                alignItems={'center'}
                gap={'0.5rem'}>
                <DropDownAppBar search={search} />
              </Box>
            </Box>
            {showHomeElements && (
              <Box
                display={'flex'}
                justifyContent={'flex-end'}
                flexGrow={isExpanded ? 1 : 0}
                zIndex={2}
                alignItems={'center'}
                gap={'0.5rem'}>
                <Paper
                  component={MotionBox}
                  bgcolor={'transparent !important'}
                  elevation={0}
                  initial='collapsed'
                  animate={isExpanded ? 'expanded' : 'collapsed'}
                  display={'flex'}
                  alignItems={'center'}
                  borderRadius={'6.25rem'}
                  sx={isExpanded ? styles().paperMobileExpanded : undefined}
                  overflow={'hidden'}>
                  <Box display={'flex'} alignItems={'center'} alignContent={'center'}>
                    <Search
                      color={buttonBackground}
                      value={search}
                      onChange={(event: any) => setSearch(event.target.value)}
                      onClear={() => setSearch('')}
                    />
                    <Image
                      src='/icons/notification.svg'
                      alt='notification'
                                      priority
                fetchPriority='high'
                      width={24}
                      height={24}
                      style={{ cursor: 'pointer' }}
                      onClick={(e: any) => setOpenNotification(e)}
                    />
                    <Box
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        router.push('/app/profile');
                      }}>
                      <Avatar
                        sx={{
                          mx: '0.4rem',
                          width: '20px',
                          height: '20px',
                          objectFit: 'cover',
                          fontSize: '0.45rem',

                          flexShrink: 0,
                        }}
                        src={`${cdn_url}${user?.picture}`}>
                        {GetInitials(user?.name, user?.lastname)}
                      </Avatar>
                    </Box>
                  </Box>
                  <NotificationBadge
                    color={'#fff'}
                    notifications={notifications?.notifications?.otherNotifications?.length || 0}
                  />
                  <DrawerComponent
                    open={openNotification}
                    onClose={() => setOpenNotification(false)}
                    listItem={notifications?.notifications?.allNotifications || []}
                  />
                </Paper>
              </Box>
            )}
          </Box>
          {search && (
            <Box display={'flex'} paddingLeft={'1rem'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography color={'black'}>
                {' '}
                Memvys that contain <b>{search}</b>{' '}
                <IconButton onClick={() => setSearch('')} disableTouchRipple disableRipple edge='end'>
                  <Image 
                                  priority
                fetchPriority='high'
                  src='/icons/close-blue.svg' alt='icon' width={18} height={18} />
                </IconButton>
              </Typography>
            </Box>
          )}
        </Box>
      </AppBar>
      
      <CancelModal open={status} onClose={switchStatus} confirmMethod={closeProcess} />
    </>
  );
}

export default MuiAppBarMobile;
